import React, { useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  alpha,
} from "@mui/material";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Sync from "@mui/icons-material/Sync";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";

import { useProgramacion } from "./hooks/useProgramacion";
import { CalendarGrid } from "../Calendario/components/CalendarGrid";
import { ServiceList } from "../Calendario/components/ServiceList";
import { normalizeCalendarEvents, getMonthName } from "../Calendario/utils";
import { getBusinessDateParts, isOvernightHour } from "../../utils/dateUtils";

const dateFromYm = (ym, fallbackOffset = 0) => {
  if (!ym) {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + fallbackOffset);
    return date;
  }
  const [year, month] = ym.split("-").map(Number);
  return new Date(year, month - 1, 1);
};

const eventsForDay = (events, day) => {
  const map = new Map();
  (events || []).forEach((event) => {
    const matches =
      event.date === day || (event.date === day + 1 && isOvernightHour(event.hour));
    if (!matches) return;
    map.set(`${event.originalServiceId}-${event.technician?._id || "none"}`, event);
  });
  return [...map.values()];
};

const MonthCard = ({
  title,
  hint,
  date,
  events,
  selectedDay,
  onSelectDay,
  ghost,
  loading,
}) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      p: 1.5,
      borderRadius: 3,
      border: ghost ? "1px dashed" : "1px solid",
      borderColor: ghost ? alpha("#64748b", 0.45) : "divider",
      bgcolor: ghost ? alpha("#f8fafc", 0.9) : "white",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      opacity: loading ? 0.7 : 1,
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, px: 0.5 }}>
      <Box minWidth={0}>
        <Typography sx={{ fontWeight: 800, fontSize: "0.92rem", textTransform: "capitalize" }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>{hint}</Typography>
      </Box>
      <Chip
        size="small"
        icon={ghost ? <VisibilityOutlined sx={{ fontSize: 14 }} /> : <CalendarMonth sx={{ fontSize: 14 }} />}
        label={ghost ? "Fantasma" : "Actual"}
        sx={{
          height: 22,
          fontWeight: 700,
          fontSize: "0.65rem",
          bgcolor: ghost ? "transparent" : alpha("#2563eb", 0.1),
          color: ghost ? "#64748b" : "#1d4ed8",
          border: ghost ? "1px dashed #94a3b8" : "none",
        }}
      />
    </Stack>
    <Box sx={{ flex: 1, minHeight: 280 }}>
      <CalendarGrid
        currentDate={date}
        events={events}
        selectedDay={selectedDay}
        onDaySelect={onSelectDay}
        compact
        ghostTheme={ghost}
      />
    </Box>
  </Paper>
);

export default function Programacion() {
  const { overview, loading, syncing, error, message, syncNow } = useProgramacion();
  const biz = getBusinessDateParts();
  const [selected, setSelected] = React.useState({
    side: "current",
    day: biz.day,
  });

  const currentDate = dateFromYm(overview?.fromMonth, 0);
  const nextDate = dateFromYm(overview?.toMonth, 1);
  const currentEvents = React.useMemo(
    () => normalizeCalendarEvents(overview?.current || []),
    [overview]
  );
  const nextEvents = React.useMemo(
    () => normalizeCalendarEvents(overview?.next || []),
    [overview]
  );

  const selectedDate = selected.side === "next" ? nextDate : currentDate;
  const selectedEvents = eventsForDay(
    selected.side === "next" ? nextEvents : currentEvents,
    selected.day
  );

  const handleSelect = useCallback((side) => (day) => {
    setSelected({ side, day });
  }, []);

  const stats = overview?.stats || { recurrent: 0, copied: 0, pending: 0 };

  return (
    <Box
      sx={{
        height: "calc(100vh - 115px)",
        p: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          bgcolor: "white",
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
            <Box
              sx={{
                p: 1,
                bgcolor: alpha("#2563eb", 0.1),
                borderRadius: 2,
                display: "flex",
              }}
            >
              <AutoAwesome sx={{ fontSize: 26, color: "primary.main" }} />
            </Box>
            <Box minWidth={0}>
              <Typography variant="body1" fontWeight={800}>
                Programación recurrente
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Los servicios mensuales se repiten solos. El mes siguiente se
                ve en gris (fantasma) y se copia en automático. Pasan a ser
                reales el 1.º día operativo de ese mes, a las 5:00 AM (hora
                México).
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={`${stats.recurrent} mensuales`} size="small" />
            <Chip
              label={`${stats.copied} ya en el próximo mes`}
              size="small"
              color="success"
              variant="outlined"
            />
            {stats.pending > 0 ? (
              <Chip
                label={`${stats.pending} por copiar`}
                size="small"
                color="warning"
              />
            ) : null}
            <Button
              size="small"
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={14} /> : <Sync />}
              onClick={syncNow}
              disabled={loading || syncing}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Sincronizar
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {message && !error ? (
        <Alert severity="success" sx={{ py: 0.3 }}>
          {message}
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={1}
        sx={{ flex: 1, minHeight: 0 }}
      >
        <MonthCard
          title={getMonthName(currentDate).toLowerCase()}
          hint="Servicios reales de este mes"
          date={currentDate}
          events={currentEvents}
          selectedDay={selected.side === "current" ? selected.day : null}
          onSelectDay={handleSelect("current")}
          ghost={false}
          loading={loading}
        />
        <MonthCard
          title={getMonthName(nextDate).toLowerCase()}
          hint="Previsualización automática del mes siguiente"
          date={nextDate}
          events={nextEvents}
          selectedDay={selected.side === "next" ? selected.day : null}
          onSelectDay={handleSelect("next")}
          ghost
          loading={loading}
        />
      </Stack>

      <Paper
        elevation={0}
        sx={{
          flex: "0 0 220px",
          minHeight: 180,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "0.82rem" }}>
            {selected.day
              ? new Date(
                  selectedDate.getFullYear(),
                  selectedDate.getMonth(),
                  selected.day
                ).toLocaleDateString("es-MX", {
                  weekday: "long",
                  day: "numeric",
                  month: "short",
                })
              : "Agenda"}
          </Typography>
          <Chip
            size="small"
            label={
              selected.side === "next"
                ? `${selectedEvents.length} proyectados`
                : `${selectedEvents.length} en agenda`
            }
            sx={{ height: 20, fontWeight: 700, fontSize: "0.65rem" }}
          />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1.1 }}>
          <ServiceList
            events={selectedEvents}
            selectedService={null}
            onSelectService={() => {}}
            loading={loading}
            selectedDay={selected.day}
          />
        </Box>
      </Paper>
    </Box>
  );
}
